/**
 * screens/modals/ClosePurposeModal
 * Close a purpose assignment.
 *
 * What happens to the unspent balance is decided by the assignment's OWN end
 * rule, chosen when it was created — not by whoever closes it. The modal states
 * the rule rather than offering a choice, because "release to holder" at close
 * time would be a way to hand someone company money without a decision trail.
 */
import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Modal, Textarea } from '../../components';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { endRuleLabel } from '../../lib/status';
import { closePurpose } from '../../services/cash/cash.service';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface ClosePurposeModalProps {
  assignmentId: string;
  /** The rule the assignment was created with. */
  endRule: string | null;
  purposeLabel: string | null;
}

export function ClosePurposeModal({
  assignmentId,
  endRule,
  purposeLabel,
}: ClosePurposeModalProps) {
  const { closeModal } = useShell();
  const [reason, setReason] = useState('');

  const { submitting, run } = useModalSubmit({
    submit: () => closePurpose(assignmentId, reason.trim() || undefined),
    title: 'Purpose closed',
    fallback: 'Nothing further can be posted to this assignment.',
  });

  return (
    <Modal
      visible
      title="Close this purpose"
      hint={purposeLabel ? `"${purposeLabel}" will be closed.` : 'The assignment will be closed.'}
      onClose={closeModal}
      primaryLabel="Close the purpose"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <View style={styles.ruleBox}>
        <Text style={styles.ruleLabel}>The end rule set when this was created</Text>
        <Text style={styles.ruleValue}>{endRuleLabel(endRule)}</Text>
      </View>

      <Textarea
        label="Reason"
        hint="Optional. Recorded against the closure."
        value={reason}
        onChangeText={setReason}
        placeholder="Trip completed"
        maxLength={240}
        rows={2}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  ruleBox: {
    padding: 13,
    backgroundColor: neutral.surface2,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.box,
    gap: 3,
  },
  ruleLabel: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  ruleValue: {
    ...sans('500'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.ink,
  },
});

export default ClosePurposeModal;
